import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ShopNav from "@/components/feature/ShopNav";
import ShopFooter from "@/components/feature/ShopFooter";
import { useCart } from "@/hooks/useCart";
import { formatPrice } from "@/lib/supabase";

const TEXT_COLOR = "#545454";
const BORDER_SOFT = "rgba(84,84,84,0.14)";
const BORDER_MID = "rgba(84,84,84,0.22)";

export default function CartPage() {
  const { lines, updateQuantity, removeLine, subtotal, totalItems } = useCart();
  const navigate = useNavigate();

  const currency = lines[0]?.currency ?? "EUR";
  const shipping = subtotal >= 80 || subtotal === 0 ? 0 : 6.9;
  const total = subtotal + shipping;

  return (
    <main
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "'Inter Tight', sans-serif",
        color: TEXT_COLOR,
      }}
    >
      <ShopNav variant="light" />

      {/* Hero-style headline block */}
      <section style={{ padding: "140px 40px 30px", maxWidth: 1280, margin: "0 auto" }}>
        <div className="flex items-center" style={{ gap: 14, marginBottom: 22 }}>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 22,
              color: "rgba(84,84,84,0.55)",
            }}
          >
            (01)
          </span>
          <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
          <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.55)" }}>
            YOUR BAG · STEP 1 OF 3 · {totalItems} {totalItems === 1 ? "PIECE" : "PIECES"}
          </span>
        </div>
        <motion.h1
          initial={{ opacity: 0, filter: "blur(12px)", y: 12 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
            fontWeight: 600,
            fontSize: 92,
            lineHeight: 0.95,
            letterSpacing: "-2.4px",
            color: TEXT_COLOR,
            margin: 0,
          }}
        >
          Your{" "}
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontWeight: 400,
              letterSpacing: "-3.8px",
            }}
          >
            little
          </span>{" "}
          basket.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          style={{
            fontSize: 14,
            color: "rgba(84,84,84,0.65)",
            marginTop: 20,
            maxWidth: 460,
            lineHeight: 1.7,
          }}
        >
          Take one last look before we tuck them in for the journey.
        </motion.p>
      </section>

      {lines.length === 0 ? (
        <section
          style={{
            padding: "80px 40px 160px",
            maxWidth: 640,
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 44,
              color: "rgba(84,84,84,0.4)",
              lineHeight: 1.1,
              marginBottom: 22,
            }}
          >
            — an empty shelf —
          </div>
          <p style={{ fontSize: 14, color: "rgba(84,84,84,0.65)", marginBottom: 28, lineHeight: 1.7 }}>
            Your cart is currently empty. Let's find you a companion.
          </p>
          <Link
            to="/catalog"
            className="cursor-pointer whitespace-nowrap inline-flex items-center transition-opacity hover:opacity-85"
            style={{
              background: TEXT_COLOR,
              color: "#FFFFFF",
              borderRadius: 999,
              padding: "14px 28px",
              fontSize: 12,
              letterSpacing: "2px",
              textDecoration: "none",
              gap: 8,
            }}
          >
            BROWSE THE COLLECTION
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-line" style={{ fontSize: 14 }}></i>
            </span>
          </Link>
        </section>
      ) : (
        <section style={{ padding: "40px 40px 100px", maxWidth: 1280, margin: "0 auto" }}>
          <div className="grid" style={{ gridTemplateColumns: "1.5fr 1fr", gap: 80, alignItems: "start" }}>
            {/* Line items */}
            <div>
              {/* Column labels */}
              <div
                className="flex items-center"
                style={{
                  padding: "0 0 16px",
                  borderBottom: `1px solid ${BORDER_MID}`,
                  fontSize: 10,
                  letterSpacing: "2px",
                  color: "rgba(84,84,84,0.55)",
                  textTransform: "uppercase",
                }}
              >
                <div style={{ flex: 1 }}>Piece</div>
                <div style={{ width: 140, textAlign: "center" }}>Quantity</div>
                <div style={{ width: 100, textAlign: "right" }}>Total</div>
              </div>

              <AnimatePresence initial={false}>
                {lines.map((line) => (
                  <motion.div
                    key={line.productId}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="flex"
                    style={{
                      gap: 20,
                      padding: "24px 0",
                      borderBottom: `1px solid ${BORDER_SOFT}`,
                      alignItems: "center",
                    }}
                  >
                    <Link
                      to={`/product/${line.productId}`}
                      className="cursor-pointer block"
                      style={{ flexShrink: 0 }}
                    >
                      <div
                        style={{
                          width: 96,
                          height: 120,
                          background: "#F6F4EF",
                          overflow: "hidden",
                          borderRadius: 3,
                        }}
                      >
                        <img
                          src={line.image}
                          alt={line.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "top",
                          }}
                        />
                      </div>
                    </Link>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Link
                        to={`/product/${line.productId}`}
                        className="cursor-pointer"
                        style={{ textDecoration: "none" }}
                      >
                        <div
                          style={{
                            fontFamily: "'Instrument Serif', serif",
                            fontStyle: "italic",
                            fontSize: 26,
                            color: TEXT_COLOR,
                            letterSpacing: "-0.6px",
                            lineHeight: 1.15,
                            fontWeight: 400,
                          }}
                        >
                          {line.name.split("—")[0]?.trim() || line.name}
                        </div>
                      </Link>
                      <div
                        style={{
                          fontSize: 11,
                          color: "rgba(84,84,84,0.55)",
                          marginTop: 6,
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                        }}
                      >
                        {formatPrice(line.unitPrice, line.currency)} EACH
                      </div>
                      <button
                        type="button"
                        onClick={() => removeLine(line.productId)}
                        className="cursor-pointer whitespace-nowrap"
                        style={{
                          background: "transparent",
                          border: 0,
                          color: "rgba(84,84,84,0.55)",
                          fontSize: 11,
                          letterSpacing: "1.5px",
                          textDecoration: "underline",
                          textUnderlineOffset: 3,
                          padding: 0,
                          marginTop: 12,
                        }}
                      >
                        REMOVE
                      </button>
                    </div>

                    <div style={{ width: 140, display: "flex", justifyContent: "center" }}>
                      <div
                        className="flex items-center"
                        style={{
                          border: `1px solid ${BORDER_MID}`,
                          borderRadius: 999,
                          padding: "3px 4px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => updateQuantity(line.productId, line.quantity - 1)}
                          className="cursor-pointer w-7 h-7 flex items-center justify-center whitespace-nowrap"
                          style={{ background: "transparent", border: 0, color: TEXT_COLOR }}
                          aria-label="decrease"
                        >
                          <i className="ri-subtract-line" style={{ fontSize: 14 }}></i>
                        </button>
                        <span
                          style={{
                            minWidth: 22,
                            textAlign: "center",
                            fontSize: 13,
                            color: TEXT_COLOR,
                          }}
                        >
                          {line.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(line.productId, line.quantity + 1)}
                          className="cursor-pointer w-7 h-7 flex items-center justify-center whitespace-nowrap"
                          style={{ background: "transparent", border: 0, color: TEXT_COLOR }}
                          aria-label="increase"
                        >
                          <i className="ri-add-line" style={{ fontSize: 14 }}></i>
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        width: 100,
                        textAlign: "right",
                        fontSize: 15,
                        fontWeight: 500,
                        color: TEXT_COLOR,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatPrice(line.unitPrice * line.quantity, line.currency)}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              <Link
                to="/catalog"
                className="cursor-pointer inline-flex items-center transition-opacity hover:opacity-70"
                style={{
                  gap: 8,
                  marginTop: 30,
                  color: TEXT_COLOR,
                  fontSize: 11,
                  letterSpacing: "2px",
                  textDecoration: "none",
                }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line" style={{ fontSize: 14 }}></i>
                </div>
                CONTINUE SHOPPING
              </Link>
            </div>

            {/* Summary */}
            <aside
              style={{
                background: "#FFFFFF",
                border: `1px solid ${BORDER_SOFT}`,
                borderRadius: 4,
                padding: "36px 32px",
                position: "sticky",
                top: 110,
              }}
            >
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 32,
                  color: TEXT_COLOR,
                  letterSpacing: "-0.8px",
                  fontWeight: 400,
                  marginBottom: 6,
                }}
              >
                Summary
              </div>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: "2px",
                  color: "rgba(84,84,84,0.5)",
                  marginBottom: 26,
                }}
              >
                A GENTLE OVERVIEW
              </div>

              <SummaryRow label="Subtotal" value={formatPrice(subtotal, currency)} />
              <SummaryRow
                label="Shipping"
                value={shipping === 0 ? "Complimentary" : formatPrice(shipping, currency)}
                italic={shipping === 0}
                hint={
                  subtotal < 80
                    ? `Add ${formatPrice(80 - subtotal, currency)} for free shipping`
                    : undefined
                }
              />

              <div style={{ height: 1, background: BORDER_SOFT, margin: "20px 0 18px" }} />

              <div className="flex items-baseline justify-between">
                <span style={{ fontSize: 12, letterSpacing: "2.5px", color: TEXT_COLOR }}>TOTAL</span>
                <span
                  style={{
                    fontFamily: "'Instrument Serif', serif",
                    fontStyle: "italic",
                    fontSize: 40,
                    color: TEXT_COLOR,
                    letterSpacing: "-1px",
                    fontWeight: 400,
                    lineHeight: 1,
                  }}
                >
                  {formatPrice(total, currency)}
                </span>
              </div>

              <button
                type="button"
                onClick={() => navigate("/checkout")}
                className="cursor-pointer whitespace-nowrap w-full transition-opacity hover:opacity-85"
                style={{
                  marginTop: 26,
                  background: TEXT_COLOR,
                  color: "#FFFFFF",
                  border: 0,
                  borderRadius: 999,
                  padding: "18px 24px",
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: "2.5px",
                }}
              >
                PROCEED TO CHECKOUT
              </button>

              <div className="flex items-center justify-center" style={{ gap: 8, marginTop: 16 }}>
                <div className="w-3 h-3 flex items-center justify-center" style={{ color: "rgba(84,84,84,0.55)" }}>
                  <i className="ri-lock-2-line" style={{ fontSize: 11 }}></i>
                </div>
                <span
                  style={{
                    fontSize: 10,
                    color: "rgba(84,84,84,0.55)",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Secure checkout · Wrapped by hand
                </span>
              </div>
            </aside>
          </div>
        </section>
      )}

      {/* Editorial signature footer */}
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 40px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          borderTop: `1px solid ${BORDER_SOFT}`,
          paddingTop: 28,
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.5)" }}>
          MAISON DES JOUETS · ATELIER
        </span>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(84,84,84,0.5)",
          }}
        >
          — made slowly, with love
        </span>
      </div>

      <ShopFooter />
    </main>
  );
}

function SummaryRow({
  label,
  value,
  hint,
  italic,
}: {
  label: string;
  value: string;
  hint?: string;
  italic?: boolean;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="flex items-center justify-between">
        <span style={{ fontSize: 13, color: "rgba(84,84,84,0.75)" }}>{label}</span>
        <span
          style={{
            fontSize: italic ? 15 : 14,
            color: TEXT_COLOR,
            fontWeight: italic ? 400 : 500,
            fontFamily: italic ? "'Instrument Serif', serif" : "'Inter Tight', sans-serif",
            fontStyle: italic ? "italic" : "normal",
            letterSpacing: italic ? "-0.3px" : "0",
          }}
        >
          {value}
        </span>
      </div>
      {hint && (
        <div
          style={{
            fontSize: 11,
            color: "rgba(84,84,84,0.5)",
            marginTop: 6,
            letterSpacing: "0.3px",
            fontStyle: "italic",
            fontFamily: "'Instrument Serif', serif",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}